import React from 'react';
import { cn } from '@/lib/utils';
import { approvalTheme, transition } from '../theme';

interface ApprovalCardProps {
  children: React.ReactNode;
  className?: string;
  variant?: 'default' | 'elevated' | 'outlined' | 'ghost' | 'info' | 'success';
  hover?: boolean;
  onClick?: () => void;
  padding?: 'none' | 'sm' | 'md' | 'lg';
}

export const ApprovalCard: React.FC<ApprovalCardProps> = ({
  children,
  className,
  variant = 'default',
  hover = false,
  onClick,
  padding = 'md',
}) => {
  const baseStyles = 'rounded-lg transition-all duration-200';
  
  const variantStyles = {
    default: 'bg-card border border-border',
    elevated: 'bg-card shadow-lg',
    outlined: 'bg-transparent border-2 border-border',
    ghost: 'bg-transparent',
    info: 'bg-blue-500/10 border border-blue-500/30',
    success: 'bg-green-500/10 border border-green-500/30',
  };

  const hoverStyles = hover
    ? 'cursor-pointer hover:border-muted-foreground hover:shadow-xl hover:scale-[1.02]'
    : '';

  const paddingStyles = {
    none: '',
    sm: 'p-4',
    md: 'p-6',
    lg: 'p-8',
  };

  return (
    <div
      className={cn(
        baseStyles,
        variantStyles[variant],
        hoverStyles,
        paddingStyles[padding],
        className
      )}
      onClick={onClick}
      style={{
        transition: transition(['all']),
      }}
    >
      {children}
    </div>
  );
};

interface ApprovalCardHeaderProps {
  children: React.ReactNode;
  className?: string;
  action?: React.ReactNode;
}

export const ApprovalCardHeader: React.FC<ApprovalCardHeaderProps> = ({
  children,
  className,
  action,
}) => {
  return (
    <div className={cn('flex items-center justify-between mb-4', className)}>
      <div>{children}</div>
      {action && <div>{action}</div>}
    </div>
  );
};

interface ApprovalCardTitleProps {
  children: React.ReactNode;
  className?: string;
}

export const ApprovalCardTitle: React.FC<ApprovalCardTitleProps> = ({
  children,
  className,
}) => {
  return (
    <h3
      className={cn('text-2xl font-bold text-foreground', className)}
      style={{
        fontSize: approvalTheme.typography.h3.fontSize,
        fontWeight: approvalTheme.typography.h3.fontWeight,
        lineHeight: approvalTheme.typography.h3.lineHeight,
      }}
    >
      {children}
    </h3>
  );
};

interface ApprovalCardDescriptionProps {
  children: React.ReactNode;
  className?: string;
}

export const ApprovalCardDescription: React.FC<ApprovalCardDescriptionProps> = ({
  children,
  className,
}) => {
  return (
    <p
      className={cn('text-muted-foreground mt-1', className)}
      style={{
        fontSize: approvalTheme.typography.bodySmall.fontSize,
        lineHeight: approvalTheme.typography.bodySmall.lineHeight,
      }}
    >
      {children}
    </p>
  );
};

interface ApprovalCardContentProps {
  children: React.ReactNode;
  className?: string;
}

export const ApprovalCardContent: React.FC<ApprovalCardContentProps> = ({
  children,
  className,
}) => {
  return <div className={cn('', className)}>{children}</div>
};

interface ApprovalCardFooterProps {
  children: React.ReactNode;
  className?: string;
}

export const ApprovalCardFooter: React.FC<ApprovalCardFooterProps> = ({
  children,
  className,
}) => {
  return (
    <div className={cn('mt-6 pt-4 border-t border-border flex items-center justify-between', className)}>
      {children}
    </div>
  );
};
